import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { serviceRequestsAPI, paymentsAPI } from '@/lib/api';
import { CreditCard, CheckCircle, Clock } from 'lucide-react';
import { trackPayment } from '@/utils/analytics';

const PaymentPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [serviceRequest, setServiceRequest] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upiRef, setUpiRef] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('pending');

  const loadPaymentData = useCallback(async () => {
    try {
      const requestRes = await serviceRequestsAPI.getById(requestId);
      setServiceRequest(requestRes.data);
      
      // Check if admin has set custom payment (payment_link and custom_amount)
      if (requestRes.data.payment_link && requestRes.data.custom_amount) {
        // Use custom payment QR and amount
        setQrData({
          qr_code: requestRes.data.payment_link,
          amount: requestRes.data.custom_amount,
          upi_id: '8892117903@ybl' // Default UPI ID
        });
      } else {
        // Use regular service price QR
        const qrRes = await paymentsAPI.getQR(requestId);
        setQrData(qrRes.data);
      }
    } catch (err) {
      console.error('Error loading payment data:', err);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadPaymentData();
  }, [loadPaymentData]);

  const handlePaymentConfirm = async () => {
    if (!upiRef.trim()) {
      alert('Please enter UPI reference number');
      return;
    }

    try {
      setPaymentStatus('processing');
      await paymentsAPI.create({
        service_request_id: requestId,
        amount: qrData.amount,
        upi_ref: upiRef
      });
      
      // Track payment conversion
      const serviceName = serviceRequest?.service_name || 'Tax Service';
      trackPayment(serviceName, qrData.amount, upiRef);
      
      setPaymentStatus('completed');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Error confirming payment:', err);
      alert('Failed to confirm payment. Please try again.');
      setPaymentStatus('pending');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'completed') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-12">
            <Clock className="text-yellow-500 mx-auto mb-6" size={80} />
            <h2 className="text-3xl font-bold mb-4" data-testid="payment-success-title">Payment Under Review</h2>
            <p className="text-gray-600 mb-2">Your payment has been submitted and is currently under review.</p>
            <p className="text-gray-500 text-sm mb-8">Our team will verify the payment shortly. Redirecting to dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" data-testid="payment-page-title">Complete Payment</h1>
          <p className="text-gray-600">Service: {serviceRequest?.service_details?.name}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* QR Code Section */}
          <div className="bg-white rounded-xl shadow-sm p-8" data-testid="qr-code-section">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <CreditCard className="text-blue-600" size={28} />
              Scan QR to Pay
            </h2>
            
            <div className="bg-gray-50 p-6 rounded-xl mb-6">
              {qrData?.qr_code && (
                <img 
                  src={qrData.qr_code} 
                  alt="UPI QR Code" 
                  className="w-full max-w-xs mx-auto"
                  data-testid="upi-qr-code"
                />
              )}
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700 font-medium">Amount:</span>
                <span className="text-2xl font-bold text-gray-900">₹{qrData?.amount}</span>
              </div>
              
              {qrData?.upi_id && (
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-gray-700 font-medium">Pay to:</span>
                  <span className="text-lg font-bold text-green-700">{qrData.upi_id}</span>
                </div>
              )}
              
              <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="font-medium mb-2">Payment Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open any UPI app (Google Pay, PhonePe, Paytm, etc.)</li>
                  <li>Scan the QR code above</li>
                  <li>Verify amount: ₹{qrData?.amount}</li>
                  <li>Complete the payment</li>
                  <li>Enter the UPI reference number below</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Payment Confirmation */}
          <div className="bg-white rounded-xl shadow-sm p-8" data-testid="payment-confirmation-section">
            <h2 className="text-2xl font-semibold mb-6">Confirm Payment</h2>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">After completing the UPI payment, please enter your transaction reference number:</p>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                UPI Reference Number *
              </label>
              <input
                type="text"
                value={upiRef}
                onChange={(e) => setUpiRef(e.target.value)}
                placeholder="e.g., 123456789012"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="upi-ref-input"
              />
            </div>

            <button
              onClick={handlePaymentConfirm}
              disabled={paymentStatus === 'processing'}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-payment-button"
            >
              {paymentStatus === 'processing' ? (
                <>
                  <Clock className="animate-spin" size={20} />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  Confirm Payment
                </>
              )}
            </button>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Your service will be activated once we verify your payment. This usually takes a few minutes.
              </p>
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div className="bg-white rounded-xl shadow-sm p-8 mt-8" data-testid="service-details-section">
          <h3 className="text-xl font-semibold mb-4">Service Details</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Service Name</p>
              <p className="font-medium">{serviceRequest?.service_details?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Price</p>
              <p className="font-medium">₹{serviceRequest?.service_details?.price}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Request ID</p>
              <p className="font-medium text-xs">{requestId}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="font-medium">{serviceRequest?.status}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;